/**
 * Compartilhamento do link de indicação dentro do webview do app.
 *
 * O `InAppWebView` do new-wallet manda para fora do app todo esquema que não
 * seja http(s) — mas SÓ se `canLaunchUrl` aceitar; senão a navegação é
 * cancelada em silêncio. E `canLaunchUrl` só aceita o que o app declarou:
 * - Android: `<queries>` tem o pacote do WhatsApp → `whatsapp://` funciona;
 *   `sms:`/`mailto:` não estão declarados.
 * - iOS: `LSApplicationQueriesSchemes` não tem whatsapp/sms/mailto → nenhum.
 * Por isso `shareChannels` mostra só o que funciona em cada lugar. Fora do app
 * (navegador comum) tudo funciona, e o WhatsApp usa `wa.me`.
 *
 * Folha nativa de compartilhamento (Instagram etc.):
 * - `navigator.share` quando o webview/navegador tem (iOS tem);
 * - ou a ponte do app: quando o new-wallet registrar o handler JS `fixeShare`
 *   (share_plus), ele também define `window.fixeNativeShare = true` — a página
 *   passa a usar a folha nativa sem nenhuma mudança aqui.
 */

export interface ShareContent {
  title: string;
  text: string;
  url: string;
}

interface FlutterBridge {
  callHandler: (name: string, ...args: unknown[]) => Promise<unknown>;
}

type ShareWindow = Window & {
  flutter_inappwebview?: FlutterBridge;
  fixeNativeShare?: boolean;
};

export function buildShareText(input: { brandName: string; benefit: string | null; url: string }): string {
  const intro = input.brandName
    ? `Estou te convidando para o programa de fidelidade da ${input.brandName}!`
    : 'Estou te convidando para um programa de fidelidade!';
  const benefit = input.benefit ? ` ${input.benefit}` : ' Cadastre-se pelo meu link.';
  return `${intro}${benefit}\n${input.url}`;
}

export function whatsappUrl(text: string, inApp: boolean): string {
  const encoded = encodeURIComponent(text);
  return inApp ? `whatsapp://send?text=${encoded}` : `https://wa.me/?text=${encoded}`;
}

/** `sms:?&body=` funciona no iOS e no Android. */
export function smsUrl(text: string): string {
  return `sms:?&body=${encodeURIComponent(text)}`;
}

export function mailtoUrl(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function isInAppWebView(win: Window, embedFlag: boolean): boolean {
  return embedFlag || Boolean((win as ShareWindow).flutter_inappwebview);
}

export function isIOS(win: Window): boolean {
  const nav = win.navigator;
  if (/iPhone|iPad|iPod/i.test(nav.userAgent)) return true;
  // iPadOS se apresenta como Mac.
  return nav.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1;
}

export type NativeShareKind = 'web' | 'bridge' | null;

export interface ShareChannels {
  whatsapp: boolean;
  sms: boolean;
  email: boolean;
  native: NativeShareKind;
}

/** O que realmente abre, no lugar onde a página está rodando. */
export function shareChannels(win: Window, embedFlag: boolean): ShareChannels {
  const native = nativeShareKind(win);
  if (!isInAppWebView(win, embedFlag)) return { whatsapp: true, sms: true, email: true, native };
  return { whatsapp: !isIOS(win), sms: false, email: false, native };
}

export function nativeShareKind(win: Window): NativeShareKind {
  const w = win as ShareWindow;
  if (typeof w.navigator?.share === 'function') return 'web';
  if (w.fixeNativeShare === true && w.flutter_inappwebview) return 'bridge';
  return null;
}

/** Folha nativa. `cancelled` quando o cliente fecha sem escolher. */
export async function nativeShare(win: Window, content: ShareContent): Promise<'shared' | 'cancelled' | 'unavailable'> {
  const kind = nativeShareKind(win);
  try {
    if (kind === 'web') {
      await win.navigator.share({ title: content.title, text: content.text, url: content.url });
      return 'shared';
    }
    if (kind === 'bridge') {
      await (win as ShareWindow).flutter_inappwebview?.callHandler('fixeShare', content);
      return 'shared';
    }
    return 'unavailable';
  } catch (err) {
    return err instanceof Error && err.name === 'AbortError' ? 'cancelled' : 'unavailable';
  }
}

/**
 * Copia com a API moderna e cai para `execCommand` — o WebView do Android
 * nem sempre expõe `navigator.clipboard`.
 */
export async function copyText(text: string, doc: Document = document): Promise<boolean> {
  try {
    if (doc.defaultView?.navigator.clipboard?.writeText) {
      await doc.defaultView.navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* tenta o fallback */
  }
  try {
    const area = doc.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    doc.body.appendChild(area);
    area.select();
    const ok = doc.execCommand('copy');
    doc.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}
